import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Chess, DEFAULT_POSITION} from "chess.js";
import socket from "../utils/clientSocket";
import ChessClock from "../ChessClock";
import GameOverModal from "./GameOverModal";
import ChessBoard from "./ChessBoard.jsx";
import Chat from "./Chat.jsx";
import Moves from "./Moves";
import PlayerStrip from "./PlayerStrip";
import Modal from "./ui/Modal";
import Icon from "./ui/Icon";
import Avatar from "./ui/Avatar";
import {useUser} from "../UserContext";
import {useToasts} from "./ui/Toasts";
import usePageTheme from "../utils/usePageTheme";
import {setActiveGame} from "../utils/activeGameStore";
import {buildPositions} from "../utils/chessFunctions";
import {playMoveSound, playSound, soundForMove, useMuted} from "../utils/sounds";
import {acceptDenyRematch, offerRematch} from "../clients/GameClient";

// Game state constants
export const gameState = {
    NOT_STARTED: "not started",
    IN_PROGRESS: "in progress",
    FINISHED: "finished",
};

const colorName = (color) => (color === "w" ? "white" : "black");

// The reasons the server sends with "gameOver"
const SERVER_REASONS = {
    checkmate: () => "by checkmate",
    stalemate: () => "by stalemate",
    "insufficient material": () => "by insufficient material",
    repetition: () => "by repetition",
    "50 move rule": () => "by the 50 move rule",
    timeout: () => "on time",
    "timeout vs insufficient material": () => "since time ran out with too little material left to checkmate",
    resignation: () => "by resignation",
    agreement: () => "by agreement",
    abandonment: (outcome) => outcome === "win" ? "because your opponent left the game" : "because you left the game"
};

const describeResult = (winner, myColor, finalFen, timeLeft, syncedAt, turnAtEnd, serverReason) => {
    const outcome = winner === "draw" ? "draw" : winner === colorName(myColor) ? "win" : "loss";
    if (serverReason && SERVER_REASONS[serverReason]) {
        return {outcome, reason: SERVER_REASONS[serverReason](outcome)};
    }
    let reason = null;
    try {
        const chess = new Chess(finalFen);
        if (chess.isCheckmate()) reason = "by checkmate";
        else if (chess.isStalemate()) reason = "by stalemate";
        else if (chess.isInsufficientMaterial()) reason = "by insufficient material";
        else if (chess.isThreefoldRepetition && chess.isThreefoldRepetition()) reason = "by repetition";
        else if (chess.isDraw()) reason = "by the 50 move rule";
    } catch (e) {
        // ignore - we just will not show a reason
    }
    if (!reason) {
        if (winner === "draw") {
            reason = "by agreement";
        } else {
            const loser = winner === "white" ? "b" : "w";
            const elapsed = turnAtEnd === loser ? Date.now() - syncedAt : 0;
            const loserTime = (timeLeft?.[loser] ?? 1) - elapsed;
            reason = loserTime <= 1000 ? "on time" : "by resignation";
        }
    }
    return {outcome, reason};
};

const NoActiveGame = () => {
    const navigate = useNavigate();
    return (
        <div className="page page--narrow">
            <div className="card card-pad" style={{textAlign: "center"}}>
                <div className="empty-state">
                    <Icon name="board"/>
                    <strong>There is no game running right now</strong>
                    <span>Head back to the lobby to start a new one, or play the computer.</span>
                    <div className="row" style={{marginTop: "0.6rem", justifyContent: "center", flexWrap: "wrap"}}>
                        <button className="btn btn-primary" onClick={() => navigate("/")}>
                            Go to the lobby
                        </button>
                        <button className="btn" onClick={() => navigate("/computer")}>
                            <Icon name="cpu"/> Play the computer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Game = () => {
    const location = useLocation();
    const state = location.state;
    if (!state || !state.gameId) return <NoActiveGame/>;
    // A new game (or a rejoin after refreshing) always gets a completely fresh component
    return <GameView key={`${state.gameId}-${state.startedAt || ""}`} initial={state}/>;
};

const GameView = ({initial}) => {
    usePageTheme("game");
    const location = useLocation();
    const navigate = useNavigate();
    const user = useUser();
    const [muted, setMuted] = useMuted();
    const {push, dismiss} = useToasts();
    const toastRef = useRef({push, dismiss});
    toastRef.current = {push, dismiss};

    const myColor = initial.color;
    const gameId = initial.gameId;
    const initialFen = initial.initialFen || DEFAULT_POSITION;
    const opponent = initial.opponent;
    const eloDeltas = initial.eloDeltas;

    const [fen, setFen] = useState(initial.fen || initialFen);
    const [moves, setMoves] = useState(initial.moves || []);
    const [currentTurn, setCurrentTurn] = useState(initial.currentTurn || "w");
    const [timeLeft, setTimeLeft] = useState(initial.gameLength || null);
    const [syncedAt, setSyncedAt] = useState(Date.now());
    const [status, setStatus] = useState(initial.gameState || gameState.IN_PROGRESS);
    const [orientation, setOrientation] = useState(initial.orientation || colorName(myColor));
    const [viewPly, setViewPly] = useState(null);
    const viewPlyRef = useRef(null);
    viewPlyRef.current = viewPly;
    const [liveLastMove, setLiveLastMove] = useState(null);
    const [drawState, setDrawState] = useState("none");
    const [confirmResign, setConfirmResign] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [result, setResult] = useState(null);
    const [rematch, setRematch] = useState({status: null, incomingId: null, error: null});
    // When the opponent closes the page during a game: {deadline} until they lose by abandonment
    const [opponentAway, setOpponentAway] = useState(null);
    const [now, setNow] = useState(Date.now());

    // Refs let the socket handlers (registered once) read the latest values
    const fenRef = useRef(fen);
    const movesRef = useRef(moves);
    const timeRef = useRef({timeLeft, syncedAt, currentTurn});
    const drawRef = useRef(drawState);
    const rematchRef = useRef(rematch);
    fenRef.current = fen;
    movesRef.current = moves;
    timeRef.current = {timeLeft, syncedAt, currentTurn};
    drawRef.current = drawState;
    rematchRef.current = rematch;

    const positions = useMemo(() => buildPositions(initialFen, moves), [initialFen, moves]);
    const isPlaying = status === gameState.IN_PROGRESS;
    const isViewingHistory = viewPly !== null && viewPly < moves.length;

    const displayed = isViewingHistory ? positions[viewPly] : null;
    const displayedFen = displayed ? displayed.fen : fen;
    const lastPosition = positions[positions.length - 1];
    const displayedLastMove = displayed
        ? displayed.move
        : (lastPosition && lastPosition.fen === fen ? lastPosition.move : liveLastMove);
    const currentPly = isViewingHistory ? viewPly : moves.length;

    // Coming back to a running game (e.g. after visiting the lobby) - the state we navigated with may be old,
    // so ask the server for the current position. The answer arrives as a fresh "gameStarted".
    useEffect(() => {
        const isStale = !initial.startedAt || Date.now() - initial.startedAt > 3000;
        if (isStale && (initial.gameState || gameState.IN_PROGRESS) === gameState.IN_PROGRESS) {
            socket.emit("requestGameState");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----- Socket events -----
    useEffect(() => {
        if (movesRef.current.length === 0 && (initial.gameState || gameState.IN_PROGRESS) === gameState.IN_PROGRESS) {
            playSound("start");
        }

        const handleUpdate = (gameData) => {
            const previousFen = fenRef.current;
            if (gameData.timeLeft) {
                setTimeLeft(gameData.timeLeft);
                setSyncedAt(Date.now());
            }
            setFen(gameData.fen);
            setCurrentTurn(gameData.currentTurn);
            if (!gameData.move) return;

            setMoves((prev) => [...prev, gameData.move]);
            const opponentMoved = gameData.currentTurn === myColor;
            if (opponentMoved) {
                // An opponent's move also declines any draw offer we made
                if (drawRef.current === "sent") setDrawState("none");
                try {
                    const chess = new Chess(previousFen);
                    const move = chess.move(gameData.move);
                    setLiveLastMove({from: move.from, to: move.to});
                    playMoveSound(move, chess.inCheck());
                } catch (e) {
                    playSound("move");
                }
            }
        };

        const handleGameOver = (data) => {
            const previousFen = fenRef.current;
            let finalMoves = movesRef.current;
            if (data.move) {
                finalMoves = [...movesRef.current, data.move];
                setMoves(finalMoves);
                try {
                    const chess = new Chess(previousFen);
                    const move = chess.move(data.move);
                    setLiveLastMove({from: move.from, to: move.to});
                    // Only the opponent's final move needs a sound - our own was played when we moved
                    if (move.color !== myColor) playSound(soundForMove(move, chess.inCheck()));
                } catch (e) {
                    // the move was already applied locally
                }
            }
            setFen(data.fen);
            setStatus(gameState.FINISHED);
            setActiveGame(null);
            setDrawState("none");
            setConfirmResign(false);

            const {timeLeft: tl, syncedAt: synced, currentTurn: turn} = timeRef.current;
            const description = describeResult(data.winningColor, myColor, data.fen, tl, synced, turn, data.reason);
            setOpponentAway(null);
            setResult(description);
            setShowResult(true);
            setTimeout(() => playSound(description.outcome === "win" ? "victory" : description.outcome === "loss" ? "defeat" : "draw"), 350);

            // Keep the finished game in the history state, so a refresh still shows the result
            navigate(location.pathname, {
                replace: true,
                state: {...initial, fen: data.fen, winner: data.winningColor, reason: data.reason, gameState: gameState.FINISHED, moves: finalMoves}
            });
        };

        const handleDrawOffer = () => {
            setDrawState("received");
            playSound("notify");
            // A toast as well, so the offer is noticed on small screens where the panel is below the board
            toastRef.current.push({
                id: "draw-offer",
                duration: 15000,
                tone: "sun",
                title: `${opponent?.username || "Your opponent"} offers a draw`,
                actions: [
                    {label: "Accept", variant: "primary", onClick: () => {
                        socket.emit("acceptDraw");
                        setDrawState("none");
                    }},
                    {label: "Decline", onClick: () => setDrawState("none")}
                ]
            });
        };

        const handleRematchOffered = (offerId) => {
            setRematch((prev) => ({...prev, incomingId: offerId}));
            setShowResult(true);
            playSound("notify");
        };
        const handleRematchDeclined = () => setRematch((prev) => ({...prev, status: "declined"}));
        const handleRematchCancelled = () => setRematch((prev) => ({...prev, incomingId: null}));
        const handleOpponentDisconnected = ({secondsLeft}) => {
            setOpponentAway({deadline: Date.now() + (secondsLeft || 60) * 1000});
            playSound("notify");
        };
        const handleOpponentReconnected = () => setOpponentAway(null);

        socket.on("update", handleUpdate);
        socket.on("gameOver", handleGameOver);
        socket.on("offerDraw", handleDrawOffer);
        socket.on("rematchOffered", handleRematchOffered);
        socket.on("rematchOfferDeclined", handleRematchDeclined);
        socket.on("rematchOfferCancelled", handleRematchCancelled);
        socket.on("opponentDisconnected", handleOpponentDisconnected);
        socket.on("opponentReconnected", handleOpponentReconnected);

        return () => {
            socket.off("update", handleUpdate);
            socket.off("gameOver", handleGameOver);
            socket.off("offerDraw", handleDrawOffer);
            socket.off("rematchOffered", handleRematchOffered);
            socket.off("rematchOfferDeclined", handleRematchDeclined);
            socket.off("rematchOfferCancelled", handleRematchCancelled);
            socket.off("opponentDisconnected", handleOpponentDisconnected);
            socket.off("opponentReconnected", handleOpponentReconnected);
            // We use the old game id when creating a rematch offer - so cancel it using the same
            if (rematchRef.current.status === "sent") {
                socket.emit("cancelRematchOffer", {offerId: gameId});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (drawState !== "received") toastRef.current.dismiss("draw-offer");
    }, [drawState]);

    useEffect(() => {
        if (!opponentAway) return;
        const interval = setInterval(() => setNow(Date.now()), 500);
        return () => clearInterval(interval);
    }, [opponentAway]);

    // If the page was refreshed after the game ended, show the result again
    useEffect(() => {
        if (initial.gameState === gameState.FINISHED && initial.winner) {
            setResult(describeResult(initial.winner, myColor, initial.fen || initialFen, null, Date.now(), null, initial.reason));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----- Moving -----
    const handleMove = useCallback((move, newFen) => {
        if (!isPlaying) return;
        setFen(newFen);
        setLiveLastMove({from: move.from, to: move.to});
        setViewPly(null);
        const after = new Chess(newFen);
        playMoveSound(move, after.inCheck());
        if (drawRef.current === "received") setDrawState("none");
        socket.emit("move", {move: {from: move.from, to: move.to, promotion: move.promotion}, gameId});
    }, [isPlaying, gameId]);

    // ----- History navigation -----
    // `silent` is used while an arrow key is held down, so the move sound does not machine-gun
    const goToPly = useCallback((ply, silent = false) => {
        const clamped = Math.max(0, Math.min(ply, movesRef.current.length));
        const next = clamped >= movesRef.current.length ? null : clamped;
        if (next === viewPlyRef.current) return;
        viewPlyRef.current = next;
        setViewPly(next);
        if (silent) return;
        const position = buildPositions(initialFen, movesRef.current.slice(0, clamped));
        const last = position[position.length - 1];
        if (last.move) playMoveSound(last.move, last.inCheck);
    }, [initialFen]);

    useEffect(() => {
        const handleKey = (event) => {
            const tag = event.target.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || event.target.isContentEditable) return;
            const current = viewPly === null ? movesRef.current.length : viewPly;
            if (event.key === "ArrowLeft") goToPly(current - 1, event.repeat);
            else if (event.key === "ArrowRight") goToPly(current + 1, event.repeat);
            else if (event.key === "Home") goToPly(0);
            else if (event.key === "End") goToPly(movesRef.current.length);
            else if (event.key === "f") setOrientation((o) => (o === "white" ? "black" : "white"));
            else return;
            event.preventDefault();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [viewPly, goToPly]);

    // ----- Actions -----
    const offerDraw = () => {
        socket.emit("offerDraw");
        setDrawState("sent");
    };
    const acceptDraw = () => {
        socket.emit("acceptDraw");
        setDrawState("none");
    };
    const resign = () => {
        socket.emit("forfeit");
        setConfirmResign(false);
    };

    const requestRematch = () => {
        setRematch((prev) => ({...prev, status: "sent", error: null}));
        offerRematch(gameId)
            .catch((error) => setRematch((prev) => ({
                ...prev,
                status: null,
                error: error.response?.data?.message || error.message
            })));
    };

    const answerRematch = (accepted) => {
        const offerId = rematch.incomingId;
        setRematch((prev) => ({...prev, incomingId: null}));
        acceptDenyRematch(offerId, accepted)
            .catch((error) => setRematch((prev) => ({...prev, error: error.response?.data?.message || error.message})));
    };

    const goToAnalysis = () => {
        navigate("/analysis", {
            state: {
                initialFen,
                moves: movesRef.current,
                playerColor: colorName(myColor),
                opponent,
                gameId: user?.isLoggedIn ? gameId : undefined
            }
        });
    };

    // ----- Layout pieces -----
    const topColor = orientation === "white" ? "b" : "w";
    const bottomColor = orientation === "white" ? "w" : "b";
    const playerFor = (color) => (color === myColor ? {...user, isActualUser: user?.isLoggedIn} : opponent);

    const clockFor = (color) => (
        <ChessClock
            timeLeft={timeLeft?.[color]}
            syncedAt={syncedAt}
            running={isPlaying && currentTurn === color}
            warn={color === myColor}
        />
    );

    const turnText = !isPlaying
        ? "Game over"
        : currentTurn === myColor ? "Your move" : `Waiting for ${opponent?.username || "opponent"}`;

    const myEloDelta = result && eloDeltas
        ? (result.outcome === "win" ? eloDeltas.winEloDelta : result.outcome === "loss" ? eloDeltas.lossEloDelta : eloDeltas.drawEloDelta)
        : null;

    return (
        <div className="page">
            <div className="game-layout">
                {/* Board */}
                <div className="game-layout__board">
                    <div className="board-column">
                        <PlayerStrip user={playerFor(topColor)} color={topColor} fen={displayedFen}
                                     isYou={topColor === myColor} clock={clockFor(topColor)}
                                     tag={topColor !== myColor && opponentAway && isPlaying ? <span className="chip chip--blossom">Left the game</span> : null}/>
                        <div style={{position: "relative"}}>
                            <ChessBoard
                                id={`game-${gameId}`}
                                fen={displayedFen}
                                orientation={orientation}
                                onMove={handleMove}
                                canMove={isPlaying && !isViewingHistory}
                                playerColor={myColor}
                                premoves
                                lastMove={displayedLastMove}
                            />
                            {isViewingHistory && (
                                <div className="history-banner">
                                    Viewing move {viewPly} of {moves.length}
                                    <button className="btn btn-sm" onClick={() => setViewPly(null)}>Back to live</button>
                                </div>
                            )}
                        </div>
                        <PlayerStrip user={playerFor(bottomColor)} color={bottomColor} fen={displayedFen}
                                     isYou={bottomColor === myColor} clock={clockFor(bottomColor)}
                                     tag={bottomColor !== myColor && opponentAway && isPlaying ? <span className="chip chip--blossom">Left the game</span> : null}/>
                    </div>
                </div>

                {/* Moves and actions */}
                <aside className="game-layout__panel">
                    <div className="card panel">
                        <div className="game-status">
                            <span className={`turn-dot turn-dot--${isPlaying ? currentTurn : myColor}`}/>
                            <div className="game-status__text">
                                <div>{turnText}</div>
                                <div className="game-status__sub">
                                    {isPlaying ? `You play ${colorName(myColor)}` :
                                        result ? `${result.outcome === "win" ? "You won" : result.outcome === "loss" ? "You lost" : "Draw"} ${result.reason || ""}` : ""}
                                </div>
                            </div>
                            <button className="icon-btn" onClick={() => setMuted(!muted)}
                                    aria-label={muted ? "Turn sound on" : "Mute sound"} title={muted ? "Sound off" : "Sound on"}>
                                <Icon name={muted ? "volumeOff" : "volume"}/>
                            </button>
                        </div>
                        {opponentAway && isPlaying && (
                                <div className="panel__section" style={{paddingTop: 0}}>
                                    <div className="inline-offer" role="status">
                                        <span className="pulse-dot"/>
                                        <span className="inline-offer__text">
                                            {opponent?.username || "Your opponent"} left the game. You win in{" "}
                                            <strong>{Math.max(0, Math.ceil((opponentAway.deadline - now) / 1000))}s</strong> unless they come back.
                                        </span>
                                    </div>
                                </div>
                            )}
                        <div style={{borderTop: "1px solid var(--line)", display: "flex", flexDirection: "column", minHeight: 0, flex: 1}}>
                            <Moves
                                moves={moves}
                                initialFen={initialFen}
                                currentPly={currentPly}
                                onSelect={goToPly}
                                extraControls={
                                    <button className="icon-btn" onClick={() => setOrientation((o) => (o === "white" ? "black" : "white"))}
                                            aria-label="Flip board" title="Flip board (F)">
                                        <Icon name="flip"/>
                                    </button>
                                }
                            />
                        </div>

                        {drawState === "received" && isPlaying && (
                            <div className="panel__section">
                                <div className="inline-offer">
                                    <Icon name="handshake"/>
                                    <span className="inline-offer__text">{opponent?.username || "Your opponent"} offers a draw</span>
                                    <div className="row">
                                        <button className="btn btn-primary btn-sm" onClick={acceptDraw}>Accept</button>
                                        <button className="btn btn-sm" onClick={() => setDrawState("none")}>Decline</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="panel__section">
                            {isPlaying ? (
                                <div className="action-row">
                                    <button className="btn btn-danger" onClick={() => setConfirmResign(true)}>
                                        <Icon name="flag"/> Resign
                                    </button>
                                    <button className="btn" onClick={offerDraw} disabled={drawState === "sent"}>
                                        <Icon name="handshake"/> {drawState === "sent" ? "Draw offered" : "Offer draw"}
                                    </button>
                                </div>
                            ) : (
                                <div className="stack stack--sm">
                                    <button className="btn btn-primary btn-block" onClick={() => setShowResult(true)}>
                                        <Icon name="trophy"/> Show result
                                    </button>
                                    <div className="action-row">
                                        <button className="btn" onClick={goToAnalysis}><Icon name="sparkles"/> Review</button>
                                        <button className="btn" onClick={() => navigate("/")}><Icon name="board"/> New game</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </aside>

                {/* Chat and stakes */}
                <aside className="game-layout__side stack">
                    {eloDeltas && (
                        <div className="card card-pad stack stack--sm">
                            <div className="versus">
                                <Avatar name={opponent?.username} size={40} guest={opponent && opponent.isActualUser === false}/>
                                <div style={{minWidth: 0}}>
                                    <div className="small muted">Playing against</div>
                                    <div style={{fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis"}}>{opponent?.username}</div>
                                </div>
                            </div>
                            <div className="elo-stakes" title="How your rating changes">
                                <div><span>Win</span><strong className="elo-plus">+{eloDeltas.winEloDelta}</strong></div>
                                <div><span>Draw</span><strong className={eloDeltas.drawEloDelta >= 0 ? "elo-plus" : "elo-minus"}>
                                    {eloDeltas.drawEloDelta > 0 ? "+" : ""}{eloDeltas.drawEloDelta}</strong></div>
                                <div><span>Loss</span><strong className="elo-minus">{eloDeltas.lossEloDelta}</strong></div>
                            </div>
                        </div>
                    )}
                    <div className="card" style={{height: 360, display: "flex", flexDirection: "column"}}>
                        <div className="card-header">
                            <div className="card-title"><Icon name="chat"/> Game chat</div>
                        </div>
                        <div style={{flex: 1, minHeight: 0}}>
                            <Chat chatType={"game"} chatId={gameId} placeholder="Say good luck…"/>
                        </div>
                    </div>
                </aside>
            </div>

            {confirmResign && (
                <Modal size="sm" title="Resign this game?" onClose={() => setConfirmResign(false)}
                       footer={<>
                           <button className="btn" onClick={() => setConfirmResign(false)}>Keep playing</button>
                           <button className="btn btn-danger" onClick={resign}><Icon name="flag"/> Resign</button>
                       </>}>
                    <div className="modal-body">
                        <p className="muted">Your opponent will be awarded the win.</p>
                    </div>
                </Modal>
            )}

            {showResult && result && (
                <GameOverModal
                    outcome={result.outcome}
                    reason={result.reason}
                    opponent={opponent}
                    eloDelta={myEloDelta}
                    onClose={() => setShowResult(false)}
                    onAnalysis={goToAnalysis}
                    onHome={() => navigate("/")}
                    rematch={rematch}
                    onOfferRematch={requestRematch}
                    onAnswerRematch={answerRematch}
                />
            )}
        </div>
    );
};

export default Game;
